import { getAnimationPreset, isAnimationPresetId, resolvePresetParams } from 'adflow';
import type { Animation, AnimationEffect, AnimationTrack } from '@adflow/types';

import { resolveSceneTiming } from '../../animationRender';
import { ANIMATION_ROLE } from './catalog';

type ElementLike = { id?: unknown; name?: unknown };

export type AnimationTrackSummary = {
  targetId: string;
  property: string;
  keyframeCount: number;
  startSeconds: number;
  endSeconds: number;
};

export type AnimationEffectSummary = {
  effectId: string;
  targetId: string;
  targetName: string | null;
  presetId: string;
  label: string | null;
  phase: 'IN' | 'OUT' | null;
  role: 'entrance' | 'exit' | null;
  property: string | null;
  params: Record<string, number | string | boolean>;
  startSeconds: number | null;
  endSeconds: number | null;
  track: AnimationTrackSummary | null;
};

export function readAnimation(state: Record<string, unknown>): Animation | null {
  const animation = state.animation as Animation | null | undefined;
  return animation && Array.isArray(animation.tracks) ? animation : null;
}

export function elementNames(state: Record<string, unknown>): Map<string, string> {
  const names = new Map<string, string>();
  if (Array.isArray(state.elements)) {
    for (const element of state.elements as ElementLike[]) {
      if (typeof element?.id === 'string') {
        names.set(element.id, typeof element.name === 'string' ? element.name : '');
      }
    }
  }
  return names;
}

const round = (value: number) => Math.round(value * 1000) / 1000;

/** The keyframes one effect generated on its target and property. */
export function effectTrackSummary(
  animation: Animation,
  effect: AnimationEffect,
): AnimationTrackSummary | null {
  if (!isAnimationPresetId(effect.presetId)) {
    return null;
  }
  const property = getAnimationPreset(effect.presetId).property;
  const track = animation.tracks.find(
    (candidate) => candidate.targetId === effect.targetId && candidate.property === property,
  );
  const keyframes = track?.keyframes.filter((keyframe) => keyframe.effectId === effect.id) ?? [];
  if (keyframes.length === 0) {
    return null;
  }
  const times = keyframes.map((keyframe) => keyframe.t);
  return {
    targetId: effect.targetId,
    property,
    keyframeCount: keyframes.length,
    startSeconds: round(Math.min(...times)),
    endSeconds: round(Math.max(...times)),
  };
}

export function describeEffect(
  animation: Animation,
  effect: AnimationEffect,
  names: Map<string, string> = new Map(),
): AnimationEffectSummary {
  const known = isAnimationPresetId(effect.presetId);
  const preset = known ? getAnimationPreset(effect.presetId) : null;
  const track = effectTrackSummary(animation, effect);
  return {
    effectId: effect.id,
    targetId: effect.targetId,
    targetName: names.get(effect.targetId) ?? null,
    presetId: effect.presetId,
    label: preset?.label ?? null,
    phase: preset?.phase ?? null,
    role: preset ? ANIMATION_ROLE[preset.phase] : null,
    property: preset?.property ?? null,
    params: preset
      ? { ...resolvePresetParams(preset, effect.params) }
      : { ...(effect.params ?? {}) },
    startSeconds: track?.startSeconds ?? null,
    endSeconds: track?.endSeconds ?? null,
    track,
  };
}

function trackView(track: AnimationTrack) {
  return {
    targetId: track.targetId,
    property: track.property,
    readOnly: true,
    keyframes: track.keyframes.map((keyframe) => ({
      t: keyframe.t,
      value: keyframe.value,
      easing: keyframe.easing,
      effectId: keyframe.effectId ?? null,
    })),
  };
}

export function animationWarnings(
  animation: Animation | null,
  state: Record<string, unknown>,
): string[] {
  if (!animation) {
    return [];
  }
  const names = elementNames(state);
  const warnings: string[] = [];
  const effects = animation.effects ?? [];
  for (const effect of effects) {
    if (!names.has(effect.targetId)) {
      warnings.push(
        `Effect ${effect.id} targets ${effect.targetId}, which is not on the template.`,
      );
    }
    if (!isAnimationPresetId(effect.presetId)) {
      warnings.push(`Effect ${effect.id} uses unknown preset ${effect.presetId}.`);
    }
  }
  const timing = resolveSceneTiming(animation);
  if (timing.durationSeconds > timing.timelineSeconds) {
    warnings.push(
      `The last keyframe is at ${timing.durationSeconds}s, after the ${timing.timelineSeconds}s timeline. Playback and previews run to the last keyframe.`,
    );
  }
  const byTarget = new Map<string, AnimationEffectSummary[]>();
  for (const effect of effects) {
    const summary = describeEffect(animation, effect, names);
    byTarget.set(effect.targetId, [...(byTarget.get(effect.targetId) ?? []), summary]);
  }
  byTarget.forEach((summaries, targetId) => {
    const entranceEnd = Math.max(
      ...summaries.filter((s) => s.phase === 'IN').map((s) => s.endSeconds ?? -Infinity),
    );
    const exitStart = Math.min(
      ...summaries.filter((s) => s.phase === 'OUT').map((s) => s.startSeconds ?? Infinity),
    );
    if (Number.isFinite(entranceEnd) && Number.isFinite(exitStart) && exitStart < entranceEnd) {
      warnings.push(
        `On ${targetId}, the exit starts at ${exitStart}s before the entrance ends at ${entranceEnd}s.`,
      );
    }
  });
  return warnings;
}

function summaryText(effects: AnimationEffectSummary[], durationSeconds: number): string {
  if (effects.length === 0) {
    return 'No animation effects.';
  }
  const lines = effects.map((effect) => {
    const name = effect.targetName ? `"${effect.targetName}"` : effect.targetId;
    const timing =
      effect.startSeconds != null && effect.endSeconds != null
        ? ` from ${effect.startSeconds}s to ${effect.endSeconds}s`
        : '';
    return `${effect.label ?? effect.presetId} on ${name}${timing}`;
  });
  return `${effects.length} effect${
    effects.length === 1 ? '' : 's'
  }, ${durationSeconds}s: ${lines.join('; ')}.`;
}

export function animationReadData(state: Record<string, unknown>) {
  const animation = readAnimation(state);
  const timing = resolveSceneTiming(animation);
  const names = elementNames(state);
  const effects = animation
    ? (animation.effects ?? []).map((effect) => describeEffect(animation, effect, names))
    : [];
  return {
    timeline: animation
      ? {
          durationSeconds: timing.timelineSeconds,
          fps: timing.fps,
          loop: timing.loop,
          readOnly: true,
        }
      : null,
    durationSeconds: timing.durationSeconds,
    effectCount: effects.length,
    effects,
    tracks: animation ? animation.tracks.map(trackView) : [],
    summary: summaryText(effects, timing.durationSeconds),
    warnings: animationWarnings(animation, state),
  };
}
